import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  User,
  Mail,
  Phone,
  Stethoscope,
  Globe2,
  Wallet,
  CheckCircle2,
  Loader2,
  Shield,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const COUNTRY_OPTIONS = [
  'Russia',
  'Georgia',
  'Kazakhstan',
  'Uzbekistan',
  'Kyrgyzstan',
  'Philippines',
  'China',
  'Bangladesh',
  'Nepal',
  'Not sure yet — help me decide',
];

const BUDGET_OPTIONS = [
  { value: 'under-30', label: 'Under ₹30 Lakh (6 years total)' },
  { value: '30-45', label: '₹30 – ₹45 Lakh' },
  { value: '45-60', label: '₹45 – ₹60 Lakh' },
  { value: 'above-60', label: 'Above ₹60 Lakh' },
  { value: 'flexible', label: 'Flexible — best value matters most' },
];

const NEET_BUCKETS = [
  { value: 'not-appeared', label: 'Not appeared yet' },
  { value: 'below-100', label: 'Below 100' },
  { value: '100-200', label: '100 – 200' },
  { value: '200-400', label: '200 – 400' },
  { value: '400-500', label: '400 – 500' },
  { value: 'above-500', label: 'Above 500' },
];

/**
 * MBBSLeadForm
 * Compact single-step lead-capture form tailored for MBBS-abroad enquiries.
 * Fields: firstName, phone (+91 default), email, NEET score bucket,
 * preferred country, budget, and consent.
 * Submits to POST /api/public/contact/submit using the existing lead pipeline,
 * with source="mbbs_program_page" so counsellors can filter these leads.
 */
export function MBBSLeadForm({
  defaultCountry = 'Russia',
  programSlug = 'mbbs-in-russia',
  onSuccess,
}) {
  const { isDark } = useTheme();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    phone: '',
    neetScore: '',
    preferredCountry: defaultCountry,
    budget: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});

  const update = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Please enter your name';
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) e.email = 'Enter a valid email';
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) e.phone = 'Enter a valid 10-digit phone number';
    if (!form.preferredCountry) e.preferredCountry = 'Pick a country';
    if (!form.budget) e.budget = 'Pick a budget range';
    if (!form.consent) e.consent = 'Please accept to proceed';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        email: form.email.trim().toLowerCase(),
        countryCode: '+91',
        phone: `+91 ${form.phone.replace(/\D/g, '')}`,
        studyDestination: form.preferredCountry,
        studyArea: 'MBBS / Medicine',
        preferredBranch: 'MBBS',
        neetScore: form.neetScore || null,
        budget: form.budget,
        programInterest: programSlug,
        source: 'mbbs_program_page',
        agreeTerms: true,
        agreeContact: form.consent,
      };
      const res = await fetch(`${API_URL}/api/public/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubmitted(true);
      toast.success("Got it — an MBBS counsellor will call you within 24 hours.");
      onSuccess?.(data);
    } catch (err) {
      toast.error('Something went wrong. Please try again or WhatsApp us.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className={`rounded-2xl p-6 text-center ${
          isDark ? 'bg-white/5 border border-white/10' : 'bg-emerald-50 border border-emerald-100'
        }`}
        data-testid="mbbs-lead-form-success"
      >
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
        <h3 className={`font-semibold text-lg mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          We&apos;ll be in touch shortly
        </h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
          One of our MBBS counsellors will call you within 24 hours with a shortlist that fits your
          NEET score, preferred country and budget.
        </p>
        <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
          Need to talk sooner? Call +91 96395 57799 — 9 AM to 7 PM IST.
        </p>
      </div>
    );
  }

  const inputBase = isDark
    ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40'
    : 'bg-white border-slate-200 text-slate-900';

  return (
    <form
      onSubmit={handleSubmit}
      className={`rounded-2xl p-6 space-y-4 ${
        isDark
          ? 'bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10'
          : 'bg-white border border-slate-200 shadow-md'
      }`}
      data-testid="mbbs-lead-form"
    >
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-ux-cyan" />
          <span className={`text-xs uppercase tracking-wider font-semibold ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>
            Free MBBS Counselling
          </span>
        </div>
        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Get Your MBBS Shortlist
        </h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
          Personalised university options in 24 hours. No pressure, no spam.
        </p>
      </div>

      {/* Name */}
      <div>
        <Label htmlFor="mbbs-name" className={`text-xs mb-1 flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <User className="w-3 h-3" /> Your name
        </Label>
        <Input
          id="mbbs-name"
          data-testid="mbbs-form-name"
          className={inputBase}
          placeholder="Full name"
          value={form.firstName}
          onChange={(e) => update('firstName', e.target.value)}
        />
        {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
      </div>

      {/* Phone + Email side-by-side on wide sidebars, stacked otherwise */}
      <div>
        <Label htmlFor="mbbs-phone" className={`text-xs mb-1 flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <Phone className="w-3 h-3" /> Phone (India +91)
        </Label>
        <Input
          id="mbbs-phone"
          data-testid="mbbs-form-phone"
          className={inputBase}
          placeholder="10-digit mobile"
          type="tel"
          inputMode="numeric"
          maxLength={10}
          value={form.phone}
          onChange={(e) => update('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
        />
        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
      </div>

      <div>
        <Label htmlFor="mbbs-email" className={`text-xs mb-1 flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <Mail className="w-3 h-3" /> Email
        </Label>
        <Input
          id="mbbs-email"
          data-testid="mbbs-form-email"
          className={inputBase}
          placeholder="you@example.com"
          type="email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
        />
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
      </div>

      {/* NEET Score */}
      <div>
        <Label className={`text-xs mb-1 flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <Stethoscope className="w-3 h-3" /> NEET score band (optional)
        </Label>
        <Select value={form.neetScore} onValueChange={(v) => update('neetScore', v)}>
          <SelectTrigger className={inputBase} data-testid="mbbs-form-neet">
            <SelectValue placeholder="Select your NEET range" />
          </SelectTrigger>
          <SelectContent>
            {NEET_BUCKETS.map((b) => (
              <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Preferred country */}
      <div>
        <Label className={`text-xs mb-1 flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <Globe2 className="w-3 h-3" /> Preferred country
        </Label>
        <Select value={form.preferredCountry} onValueChange={(v) => update('preferredCountry', v)}>
          <SelectTrigger className={inputBase} data-testid="mbbs-form-country">
            <SelectValue placeholder="Pick a country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_OPTIONS.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.preferredCountry && <p className="text-xs text-red-500 mt-1">{errors.preferredCountry}</p>}
      </div>

      {/* Budget */}
      <div>
        <Label className={`text-xs mb-1 flex items-center gap-1.5 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          <Wallet className="w-3 h-3" /> Total budget for 6 years
        </Label>
        <Select value={form.budget} onValueChange={(v) => update('budget', v)}>
          <SelectTrigger className={inputBase} data-testid="mbbs-form-budget">
            <SelectValue placeholder="Pick a budget range" />
          </SelectTrigger>
          <SelectContent>
            {BUDGET_OPTIONS.map((b) => (
              <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.budget && <p className="text-xs text-red-500 mt-1">{errors.budget}</p>}
      </div>

      {/* Consent */}
      <div className="flex items-start gap-2 pt-1">
        <Checkbox
          id="mbbs-consent"
          data-testid="mbbs-form-consent"
          checked={form.consent}
          onCheckedChange={(v) => update('consent', !!v)}
          className="mt-0.5"
        />
        <label
          htmlFor="mbbs-consent"
          className={`text-xs cursor-pointer ${isDark ? 'text-white/60' : 'text-slate-500'}`}
        >
          I&apos;m happy to be contacted by an MBBS counsellor over call, WhatsApp or email.
        </label>
      </div>
      {errors.consent && <p className="text-xs text-red-500 -mt-2">{errors.consent}</p>}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue text-white hover:opacity-90 disabled:opacity-60"
        data-testid="mbbs-form-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending…
          </>
        ) : (
          'Get My Free MBBS Shortlist'
        )}
      </Button>

      <div className={`flex items-center justify-center gap-1.5 text-[11px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
        <Shield className="w-3 h-3" />
        Your details stay with us — never sold, never shared.
      </div>
    </form>
  );
}

export default MBBSLeadForm;
